import http from "../general/HttpClient";

export interface CurriculumReference {
  standardID: string;
  standardVersion: string;
  gradeCode: string | null;
  subjectCode: string | null;
  outcomeCode: string | null;
}

export interface CurriculumEntity {
  kind: string;
  id: string;
  name: string;
  curriculum: CurriculumReference;
  templateKey: string | null;
  templateItemID: string | null;
  templateRevision: number | null;
  version: number;
}

export interface CurriculumMappingDraft extends CurriculumReference {
  templateKey: string | null;
  templateItemID: string | null;
  templateRevision: number | null;
  expectedVersion: number;
}

export interface ReportingMeasureDraft {
  name: string;
  qualificationModuleID: string;
  entityType: string;
  entityID: string;
  markTypeID: string;
  comparisonKind: string;
  comparisonDefinitionID: string;
  comparisonVersion: string;
  finalizedOnly: boolean;
  moderation: string;
  enabled: boolean;
  expectedVersion?: number;
}

export interface ReportingMeasure extends ReportingMeasureDraft {
  id: string;
  version: number;
}

export interface ReportingSnapshot {
  snapshotID: string;
  measureID: string;
  revision: number;
  capturedAt: string;
  replacesSnapshotID: string | null;
  enrollments: Array<{
    studentID: string;
    missingReason: string | null;
    result: { result: number; total: number } | null;
  }>;
}

const base = "/api/assessment/curriculum-reporting";

export async function entities(kind: string): Promise<CurriculumEntity[]> {
  return (await http.get(`${base}/entities`, { params: { kind } })).data;
}

export async function saveMapping(
  kind: string,
  id: string,
  draft: CurriculumMappingDraft,
): Promise<CurriculumEntity> {
  return (
    await http.put(
      `${base}/entities/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/curriculum`,
      {
        ...draft,
        gradeCode: draft.gradeCode || null,
        subjectCode: draft.subjectCode || null,
      },
    )
  ).data;
}

export async function measures(): Promise<ReportingMeasure[]> {
  return (await http.get(`${base}/measures`)).data;
}

export async function saveMeasure(
  id: string | null,
  draft: ReportingMeasureDraft,
): Promise<ReportingMeasure> {
  const request: ReportingMeasureDraft = {
    name: draft.name.trim(),
    qualificationModuleID: draft.qualificationModuleID.trim(),
    entityType: draft.entityType,
    entityID: draft.entityID.trim(),
    markTypeID: draft.markTypeID.trim(),
    comparisonKind: draft.comparisonKind,
    comparisonDefinitionID: draft.comparisonDefinitionID.trim(),
    comparisonVersion: draft.comparisonVersion.trim(),
    finalizedOnly: draft.finalizedOnly,
    moderation: draft.moderation,
    enabled: draft.enabled,
    ...(id ? { expectedVersion: draft.expectedVersion } : {}),
  };
  try {
    return id
      ? (await http.put(`${base}/measures/${encodeURIComponent(id)}`, request))
          .data
      : (await http.post(`${base}/measures`, request)).data;
  } catch (failure) {
    const errors = (
      failure as { response?: { data?: { errors?: Record<string, string[]> } } }
    ).response?.data?.errors;
    if (errors) {
      const messages = Object.values(errors).flat().filter(Boolean);
      if (messages.length > 0) {
        throw new Error(messages.join(" "));
      }
    }
    throw failure;
  }
}

export async function snapshots(
  measureID: string,
): Promise<ReportingSnapshot[]> {
  return (
    await http.get(
      `${base}/measures/${encodeURIComponent(measureID)}/snapshots`,
    )
  ).data;
}

export async function capture(
  measureID: string,
  requestID: string,
  expectedPreviousSnapshotID: string | null,
): Promise<ReportingSnapshot> {
  return (
    await http.post(
      `${base}/measures/${encodeURIComponent(measureID)}/snapshots`,
      {
        requestID,
        expectedPreviousSnapshotID,
      },
    )
  ).data;
}

export function snapshotURL(snapshotID: string): string {
  return `${base}/snapshots/${encodeURIComponent(snapshotID)}`;
}
